// src/components/ClaudeChat.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ClaudeChat.css';
import EmotionClassifier from '../utils/EmotionClassifier';
import emotions from '../utils/emotions';

const classifier = new EmotionClassifier();
const MAX_CHAT_TURNS = 7;

const ClaudeChat = ({ 
  userInfo, 
  onEmotionResult, 
  messages, 
  setMessages, 
  emotionHistory // Add this prop 
}) => {
  const { firstName } = userInfo;
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = React.useRef(null);
  const messagesEndRef = React.useRef(null);
  const [selectedRole] = useState('EMOTIONAL_GRANULARITY_EXPERT');
  const [chatTurn, setChatTurn] = useState(0);
  const [inputDisabled, setInputDisabled] = useState(false);

  // scroll and focus effects
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (chatTurn  >= (MAX_CHAT_TURNS)) {
      setInputDisabled(true);
    }
  }, [chatTurn]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffInMinutes = Math.floor((now - then) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes} minutes`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    
    // Check if we've reached the maximum turns
    if (chatTurn >= MAX_CHAT_TURNS) {
      setInputDisabled(true);
      return;
    }

    setIsLoading(true);
    const userMessage = { text: input, sender: 'user' };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');

    // (Optional) simulate delay in development.
    if (process.env.NODE_ENV === 'development') {
      await new Promise(resolve => setTimeout(resolve, 1500));
    }

    // For chatTurn 0, use a simple prompt.
    if (chatTurn === 0) {
      // Find last non-discarded emotion from history
      const lastEmotion = emotionHistory?.find(e => !e.isDiscarded);
      
      let initialPrompt = 'How are you feeling today?';
      
      if (lastEmotion) {
        const emotion = emotions.find(e => e.id === lastEmotion.emotion_result);
        const timeAgo = formatTimeAgo(lastEmotion.created_at);
        
        initialPrompt = `The last recorded emotion from ${firstName} was ${emotion.name.toLowerCase()}, ${timeAgo} ago. Now, we are starting a new chat with ${firstName}. If user asks about last emotions, do not trigger a confident classification with that emotion, user just wants to learn how they felt before this chat. Follow the system prompt above strictly and provide a great experience.`;
      }

      // Add visible: false to the initial prompt message
      const updatedMessages = [
        ...messages, 
        { text: initialPrompt, sender: 'ai', visible: false }, 
        userMessage
      ];
      setMessages(updatedMessages);

      const requestMessages = [
        { role: 'assistant', content: initialPrompt },
        { role: 'user', content: input }
      ];
      
      setChatTurn(chatTurn + 1);
      try {
        console.log('Requesting AI response...');
        console.log(requestMessages);
        console.log("-------------------------------")
        const response = await axios.post('/api/messages', {
          model: "claude-3-5-sonnet-20241022",
          messages: requestMessages,
          role: selectedRole,
          max_tokens: 2048
        });
        const aiResponse = response.data.content.trim();
        const cleanResponse = aiResponse.replace(/<thinking>[\s\S]*?<\/thinking>/g, '');
        const parser = new DOMParser();
        const finalResponse = parser.parseFromString(cleanResponse, 'text/html').body.textContent.trim();
        setMessages([...updatedMessages, { text: finalResponse, sender: 'ai' }]);
      } catch (error) {
        console.error(error);
        setMessages([...updatedMessages, { text: 'Sorry, I am struggling to process your request right now. Try again later?', sender: 'ai' }]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // For chatTurn 1 and onwards, try forced JSON classification first.

    // Get result
    const forcedResult = await classifier.tryForcedClassification(input, messages, selectedRole);

    // If there's a result, process it and return.
    if (forcedResult && forcedResult.emotionData) {
      setMessages(prev => [
        ...prev,
        { 
          text: forcedResult.introMessage || "Thinking...", 
          sender: 'ai',
          isLoading: true  // Add this flag
        }
      ]);
      onEmotionResult(forcedResult.emotionData, newMessages);
      setChatTurn(chatTurn + 1);
      setIsLoading(false);
      return;
    }
    // Otherwise, fall back to a normal request.
    let fallback;
    try {
      let prefillType = 'no_prefill';
      switch (chatTurn) {
        case MAX_CHAT_TURNS - 2:
          prefillType = 'conclusive';
          break;
        case MAX_CHAT_TURNS - 1:
          // prefill with 'force_conclusive' for MAX_CHAT_TURNS - 1
          prefillType = 'force_conclusive';
          break;
        default:
          // default to 'no_prefill'
          prefillType = 'no_prefill';
      }

      fallback = await classifier.normalClassification(
        input,
        messages,
        selectedRole,
        prefillType
      );
      if (!fallback) {
        throw new Error('Fallback classification failed');
      }

      const fallbackResponse = fallback.responseData.content.trim();
      const cleanResponse = fallbackResponse.replace(/<thinking>[\s\S]*?<\/thinking>/g, '');
      const parser = new DOMParser();
      const finalResponse = parser.parseFromString(cleanResponse, 'text/html').body.textContent.trim();
      setMessages([...newMessages, { text: finalResponse, sender: 'ai' }]);
      setChatTurn(chatTurn + 1);
    } catch (error) {
      console.error(error);
      setMessages([...newMessages, { text: 'Sorry, I am struggling to process your request right now. Try again later?', sender: 'ai' }]);
      setChatTurn(chatTurn + 1);
    } finally {
      setIsLoading(false);
    }
  };

  const renderLoadingMessage = (text = "Thinking") => (
    <div className="message ai">
      <div className="message-content">
        <div className="thinking-animation" data-text={text}>
          {/* The content and dots will be handled by CSS */}
        </div>
      </div>
    </div>
  );

  return (
    <div className="chat-container">
      {messages.length > 0 && (
        <div className="chat-messages">
          {messages
            .filter(message => message.visible !== false)
            .map((message, index) => (
              <div key={index} className={`message ${message.sender}`}>
                {message.isLoading ? (
                  renderLoadingMessage("Getting granular")
                ) : (
                  <div className="message-content" dangerouslySetInnerHTML={{ __html: message.text }}></div>
                )}
              </div>
            ))}
          {isLoading && renderLoadingMessage("Thinking")}
          <div ref={messagesEndRef} />
        </div>
      )}
      <div className="chat-input-container">
        <form onSubmit={handleSubmit} className="chat-input">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your answer..."
            rows="1" 
            className="chat-textarea"
            disabled={inputDisabled}
          />
          <button 
            type="submit" 
            disabled={isLoading && !inputDisabled}
            className={`send-button ${inputDisabled ? 'restart-pulse' : ''}`}
            onClick={inputDisabled ? () => {
              setMessages([]);
              setChatTurn(0);
              setInputDisabled(false);
            } : handleSubmit}
            aria-label={inputDisabled ? "Start new conversation" : "Send message"}
          >
            {inputDisabled ? (
              <svg viewBox="0 0 1200 1200">
                <path d="m600 222.14c101.55 0 196.81 40.293 267.19 110.67 12.695 12.695 24.457 26.258 35.195 40.566l-0.09375-75.668c0-19.375 14.586-35.348 33.379-37.531l4.4062-0.25391c19.375 0 35.348 14.586 37.531 33.379l0.25391 4.4062v188.93c0 19.379-14.586 35.348-33.379 37.531l-4.4062 0.25391h-188.93c-20.867 0-37.785-16.918-37.785-37.785 0-19.379 14.586-35.348 33.379-37.531l4.4062-0.25391 110.71 0.007813c-13.207-22.828-29.383-43.887-48.109-62.613-56.348-56.348-132.45-88.539-213.75-88.539-166.95 0-302.29 135.34-302.29 302.29 0 166.95 135.34 302.29 302.29 302.29 81.297 0 157.4-32.191 213.75-88.539 28.074-28.074 50.414-61.391 65.617-98.051 7.5586-18.227 13.312-37.188 17.168-56.664 4.0469-20.473 23.926-33.785 44.398-29.734s33.785 23.93 29.734 44.398c-4.8242 24.383-12.031 48.125-21.492 70.945-19.016 45.852-46.934 87.484-81.988 122.54-70.379 70.379-165.64 110.67-267.19 110.67-208.68 0-377.86-169.17-377.86-377.86 0-208.68 169.17-377.86 377.86-377.86z" />
              </svg>
            ) : isLoading ? (
              <svg viewBox="0 0 24 24">
                <path d="M6 6 L18 6 L18 18 L6 18 Z" fill="#fff3e0" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24">
                <path d="M4 20 L20 20 L12 8 Z" />
              </svg>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ClaudeChat;
